import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Trash2, Bot, Sparkles, X, Mic, Square } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIAssistantPanel({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => `s-${Date.now()}`);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 100);
  }, []);

  useEffect(() => {
    if (!open) return;
    setMessages([]);
  }, [open]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        if (blob.size < 1000) { toast.error('Recording too short'); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          const ext = mr.mimeType.includes('webm') ? 'webm' : 'm4a';
          fd.append('audio', blob, `voice.${ext}`);
          const { data } = await axios.post(`${API}/ai-assistant/transcribe`, fd, { withCredentials: true });
          if (data.text) setInput(prev => prev ? prev + ' ' + data.text : data.text);
          else toast.error('Could not understand audio');
        } catch { toast.error('Transcription failed'); }
        setTranscribing(false);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const toggleVoice = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    scrollToBottom();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai-assistant/chat`, { message: msg, session_id: sessionId }, { withCredentials: true });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      scrollToBottom();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed';
      if (detail.includes('Premium')) {
        toast.error('AI Assistant is a Premium feature. Upgrade your plan to access.');
        onClose();
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
      }
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const clearHistory = async () => {
    await axios.delete(`${API}/ai-assistant/history?session_id=${sessionId}`, { withCredentials: true }).catch(() => {});
    setMessages([]);
    setSessionId(`s-${Date.now()}`);
  };

  if (!open) return null;

  return (
    <div className="fixed bottom-24 right-3 left-3 lg:bottom-6 lg:right-6 lg:left-auto z-[9999] lg:w-[340px] h-[50vh] lg:h-[480px] bg-white rounded-2xl shadow-2xl border border-[#E5E7E1] flex flex-col overflow-hidden" data-testid="ai-panel" style={{ boxShadow: '0 12px 48px rgba(0,0,0,0.15)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-2.5 bg-gradient-to-r from-[#1A2421] to-[#2D3B2D] text-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles size={16} />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>AI Assistant</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button onClick={clearHistory} className="p-1.5 rounded-lg hover:bg-white/15 transition-colors" data-testid="ai-clear"><Trash2 size={14} /></button>
          )}
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15 transition-colors" data-testid="ai-close"><X size={14} /></button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Sparkles size={24} className="text-[#556B2F] mb-2" />
            <p className="text-sm font-semibold text-[#1A2421] mb-1">How can I help?</p>
            <p className="text-xs text-[#5C6656] mb-4">Ask about your schedule, revenue, or clients</p>
            <div className="space-y-1.5 w-full">
              {["What's my schedule today?", "How much revenue this week?", "Who's the busiest today?", "Any gaps in today's bookings?"].map((q, i) => (
                <button key={i} onClick={() => sendMessage(q)} className="w-full text-left text-xs px-3 py-2 rounded-lg border border-[#E5E7E1] hover:bg-[#F5F7F0] text-[#5C6656] transition-colors" data-testid={`ai-suggest-${i}`}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-md bg-[#556B2F] flex items-center justify-center flex-shrink-0 mt-0.5"><Bot size={12} className="text-white" /></div>
            )}
            <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${msg.role === 'user' ? 'bg-[#1A2421] text-white rounded-br-sm' : 'bg-[#F5F7F0] text-[#1A2421] rounded-bl-sm'}`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <div className="w-6 h-6 rounded-md bg-[#556B2F] flex items-center justify-center flex-shrink-0"><Bot size={12} className="text-white" /></div>
            <div className="bg-[#F5F7F0] px-3 py-2.5 rounded-xl rounded-bl-sm">
              <div className="flex gap-1"><div className="w-1.5 h-1.5 rounded-full bg-[#556B2F] animate-bounce" style={{ animationDelay: '0ms' }} /><div className="w-1.5 h-1.5 rounded-full bg-[#556B2F] animate-bounce" style={{ animationDelay: '150ms' }} /><div className="w-1.5 h-1.5 rounded-full bg-[#556B2F] animate-bounce" style={{ animationDelay: '300ms' }} /></div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex-shrink-0 px-3 py-2.5 border-t border-[#E5E7E1] bg-white">
        <div className="flex items-center gap-1.5">
          <button onClick={toggleVoice} disabled={transcribing} className={`p-2 rounded-lg transition-colors flex-shrink-0 ${recording ? 'bg-[#E2725B] text-white' : transcribing ? 'bg-[#556B2F]/20 text-[#556B2F]' : 'hover:bg-[#F5F7F0] text-[#5C6656]'}`} data-testid="ai-voice">
            {recording ? <Square size={16} /> : transcribing ? <div className="w-4 h-4 border-2 border-[#556B2F] border-t-transparent rounded-full animate-spin" /> : <Mic size={16} />}
          </button>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={recording ? 'Recording... tap to stop' : transcribing ? 'Transcribing...' : 'Type or speak...'}
            className="flex-1 h-9 px-3 rounded-lg border border-[#E5E7E1] text-xs focus:outline-none focus:ring-1 focus:ring-[#556B2F]/30 bg-[#FAFAF8]"
            disabled={loading || transcribing}
            data-testid="ai-input"
          />
          <button onClick={() => sendMessage()} disabled={!input.trim() || loading} className="p-2 rounded-lg bg-[#1A2421] text-white disabled:opacity-30 flex-shrink-0" data-testid="ai-send">
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
